import React from 'react'

import Intro          from '../Intro'
import Amount         from '../fiat/Amount'
import PaymentOptions from '../fiat/PaymentOptions'
import Contact        from '../fiat/Contact'
import InputField     from 'src/components/InputField'
import DataSharing    from '../DataSharing'
import Tribute        from '../Tribute'
import useDonation    from '../fiat/useDonation'
import useRecord      from '../fiat/useRecord'
import useValidations from '../fiat/useValidations'
import useBankInfo    from './useBankInfo'
import usePayment     from './usePayment'

import useBeneficiarySelect from '../useBeneficiarySelect'
import useModalLock from 'src/widgets/useModalLock'

const App = ({
  partner_item, partner_items, goal_partner_item, partner, i18n, defaultShares,
  submitPath, foundation, partner_is_pledge, data_sharing, tribute, setDonation,
  amount, currencies, presets, min_amount, blank_amount, order,
  collect_address, collect_phone, session, googleApiKey, token, template, panel,
  show_input_labels, traditional_flat_style, disable_donation_split, elements,
  donationTags, embeddedOn, commonInputProps, plugins, pledge_id, address, ein,
  paymentOption, donationsPath,
}) => {
  const { partnerItem, projectId, ...beneficiarySelect } = useBeneficiarySelect({
    i18n, commonInputProps, partner_item, partner_items, goal_partner_item,
    disable_donation_split,
  })

  const donation = useDonation({
    amount, currencies, presets, min_amount, blank_amount,
    order, collect_address, session,
    tipping: { type: 'none' },
  })

  const record = useRecord({
    i18n, order, partner, partnerItem, projectId, pledge_id, session, tribute,
    donationTags, embeddedOn, donation, plugins, donationsPath,
    widget: { token, template },
  })

  // Lock the host modal while the deferred payment submits (wire/check/
  // pledge). record.processing is set by usePayment's sendPayment and
  // cleared in its finally.
  useModalLock(record.processing)

  const validations = useValidations({
    i18n, session, collect_phone,
    currency: donation.currency,
    minAmount: donation.minAmount,
  })

  const bankInfo = useBankInfo({
    i18n, currency: donation.currency, address, ein, paymentOption
  })

  const actuallyCollectAddress =
    donation.collectAddress && bankInfo.state.paymentOption != 'tbd'

  const inputProps = (id, label, {
    transformValue,
    showInputLabels = show_input_labels || actuallyCollectAddress,
    validation = validations.contact,
    state = donation.state,
    dispatch = donation.dispatch,
  } = {}) => ({
    ...commonInputProps(label, showInputLabels),
    id,
    value: state[id],
    onChange: e => {
      const value = transformValue?.(e.target.value) ?? e.target.value
      dispatch({ type: 'setFieldValue', field: id, value })
      validation.resetErrorsFor(id)
    },
  })

  const validateAll = () => {
    // Make sure all validations run
    const results = [
      beneficiarySelect.validate(),
      validations.donation.validate(donation.state),
      validations.contact.validate(donation.state),
      bankInfo.validation.validate(bankInfo.state),
      tribute.validate(),
      plugins.validate({ donation }),
    ]
    return results.length == results.filter(Boolean).length
  }

  const payment = usePayment({
    i18n, partnerItem, donation, record, bankInfo, submitPath,
    finished: setDonation,
    failed: ({ last4, bank_name, ...errors }) => {
      validations.contact.setErrors(errors)
      bankInfo.validation.setErrors({ last4, bankName: bank_name })
    },
  })

  const validateAndSubmit = ({ complete }) => {
    const results = validateAll()
    if (results && complete)
      payment.sendPayment()
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (record.processing) return

    if (actuallyCollectAddress)
      elements.getElement('address').getValue().then(validateAndSubmit)
    else
      validateAndSubmit({ complete: true })
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Intro i18n={i18n} />

      <p className="bg-light px-3 py-2 rounded-3">
        {i18n.t('deferred.explanation')}
      </p>

      {plugins.renderError()}
      {plugins.renderHook('afterIntro', 'deferred', { donation })}

      <Tribute tribute={tribute} />

      <h2 className="h5 section-heading section-heading-donation">
        {i18n.t('donation')}
      </h2>

      {beneficiarySelect.render()}

      <Amount
        i18n={i18n}
        donation={donation}
        currencies={currencies}
        commonInputProps={commonInputProps}
        errors={validations.donation.errors}
        show_input_labels={show_input_labels}
        traditional_flat_style={traditional_flat_style}
        multiOrg={beneficiarySelect.multiOrg}
        multiProject={beneficiarySelect.multiProject}
      />

      {plugins.renderHook('afterAmounts', 'deferred', { donation })}
      {plugins.renderHook('afterSubscription', 'deferred', { donation })}

      <h2 className="h5 mt-4 section-heading section-heading-payment">
        {i18n.t('payment')}
      </h2>

      <PaymentOptions
        i18n={i18n}
        paymentOptions={{ wire: {}, check: {}, tbd: {} }}
        paymentOption={bankInfo.state.paymentOption}
        setPaymentOption={bankInfo.setPaymentOption}
        show_input_labels={show_input_labels}
        errors={{ ...validations.contact.errors, ...bankInfo.validation.errors }}
      />

      <Contact
        i18n={i18n}
        session={session}
        collect_phone={collect_phone}
        show_input_labels={show_input_labels}
        donation={{ ...donation, collectAddress: actuallyCollectAddress }}
        inputProps={inputProps}
        googleApiKey={googleApiKey}
        errors={validations.contact.errors}
        hideAddress={[ 'fiat', 'crypto' ].includes(panel)}
      />

      {
        bankInfo.state.paymentOption == 'wire' &&
        <div className="row gx-2 row-bank-info">
          <div className="col-sm">
            <InputField
              {...inputProps('bankName', i18n.t('bank_name'), bankInfo)}
              errors={bankInfo.validation.errors}
              required
              autoComplete="off"
            />
          </div>
          <div className="col-sm">
            <InputField
              {...inputProps('last4', i18n.t('bank_last4'), bankInfo)}
              errors={bankInfo.validation.errors}
              required
              autoComplete="off"
            />
          </div>
        </div>
      }

      <div className="mb-3">
        <DataSharing
          i18n={i18n}
          foundation={foundation}
          beneficiary={partnerItem.beneficiary}
          partner={partner}
          partner_is_pledge={partner_is_pledge}
          data_sharing={data_sharing}
          defaultShares={defaultShares}
          shares={donation.shares}
          setShares={donation.setShares}
          collect_phone={collect_phone}
          collect_address={collect_address}
        />
      </div>
      {plugins.renderHook('afterDataSharing', 'deferred', { donation })}

      <div className="d-grid mb-4">
        <button className="btn btn-lg btn-primary" disabled={record.processing}>
          {record.processing ? 'Donating...' : 'Donate'}
        </button>
      </div>

      {bankInfo.render()}
    </form>
  )
}

export default App
